import { differenceInCalendarDays, format, parseISO } from 'date-fns'
import { tr } from 'date-fns/locale'
import { BellOff, CheckCheck, ExternalLink, Inbox, Mail, MailOpen, Search, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router'
import { toast } from 'sonner'
import { IconAction } from '@/components/common/IconAction'
import { PageHeader } from '@/components/common/PageHeader'
import { Pager } from '@/components/common/Pager'
import { EmptyState, ErrorState } from '@/components/common/States'
import { usePagination } from '@/components/common/usePagination'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { confirmModal } from '@/components/ui/modal'
import { Select } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { cn } from '@/lib/utils'
import { useMarkAllRead, useNotificationList, useRemoveNotification, useRemoveRead, useSetRead } from './api'
import { NOTIFICATION_CATEGORIES, type AppNotification, type NotificationCategory, type NotificationLevel, type ReadFilter } from './types'
import { CATEGORY_ICON, LEVEL_TONE } from './visuals'

const READ_FILTERS: ReadFilter[] = ['all', 'unread', 'read']
const LEVELS: NotificationLevel[] = ['critical', 'warning', 'success', 'info']

function bucketOf(iso: string) {
  const d = differenceInCalendarDays(new Date(), parseISO(iso))
  return d <= 0 ? 'today' : d === 1 ? 'yesterday' : d < 7 ? 'week' : 'older'
}

export function NotificationsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const q = useNotificationList()
  const setRead = useSetRead()
  const markAll = useMarkAllRead()
  const remove = useRemoveNotification()
  const removeRead = useRemoveRead()
  const [params, setParams] = useSearchParams()
  const filter: ReadFilter = (READ_FILTERS as string[]).includes(params.get('durum') ?? '') ? (params.get('durum') as ReadFilter) : 'all'
  const setFilter = (v: ReadFilter) =>
    setParams(
      (prev) => {
        const n = new URLSearchParams(prev)
        if (v === 'all') n.delete('durum')
        else n.set('durum', v)
        return n
      },
      { replace: true },
    )
  const [category, setCategory] = useState<NotificationCategory | 'all'>('all')
  const [level, setLevel] = useState<NotificationLevel | 'all'>('all')
  const [search, setSearch] = useState('')

  const all = q.data ?? []
  const counts = { all: all.length, unread: all.filter((n) => !n.readAt).length, read: all.filter((n) => !!n.readAt).length }
  const term = search.trim().toLocaleLowerCase('tr')
  const rows = all.filter(
    (n) =>
      (filter === 'all' || (filter === 'unread' ? !n.readAt : !!n.readAt)) &&
      (category === 'all' || n.category === category) &&
      (level === 'all' || n.level === level) &&
      (!term || `${n.title} ${n.body} ${n.actor}`.toLocaleLowerCase('tr').includes(term)),
  )
  const { pageRows, pager } = usePagination(rows, [filter, category, level, search])
  const groups: [string, AppNotification[]][] = []
  for (const n of pageRows) {
    const b = bucketOf(n.createdAt)
    const last = groups.at(-1)
    if (last && last[0] === b) last[1].push(n)
    else groups.push([b, [n]])
  }

  const onRemoveRead = async () => {
    const ok = await confirmModal({ title: t('notifications.removeReadTitle'), description: t('notifications.removeReadBody', { count: counts.read }), confirmLabel: t('common.delete'), destructive: true })
    if (ok) removeRead.mutate(undefined, { onSuccess: () => toast.success(t('notifications.removedRead')) })
  }

  return (
    <>
      <PageHeader
        title={t('notifications.title')}
        description={t('notifications.subtitle')}
        actions={
          <>
            <Button variant="outline" onClick={() => markAll.mutate(undefined, { onSuccess: () => toast.success(t('notifications.allMarked')) })} disabled={!counts.unread || markAll.isPending}>
              <CheckCheck />
              {t('notifications.markAllRead')}
            </Button>
            <Button variant="outline" className="text-destructive hover:text-destructive" onClick={() => void onRemoveRead()} disabled={!counts.read || removeRead.isPending}>
              <Trash2 />
              {t('notifications.removeRead')}
            </Button>
          </>
        }
      />

      {q.isError ? (
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      ) : !q.data ? (
        <div className="space-y-3">
          <Skeleton className="h-12 rounded-xl" />
          <Skeleton className="h-96 rounded-xl" />
        </div>
      ) : (
        <div className="space-y-4">
          <Tabs value={filter} onValueChange={(v) => setFilter(v as ReadFilter)}>
            <TabsList>
              {READ_FILTERS.map((f) => (
                <TabsTrigger key={f} value={f}>
                  {f === 'unread' ? <Mail /> : f === 'read' ? <MailOpen /> : <Inbox />}
                  {t(`notifications.filter.${f}`)}
                  <span className={cn('rounded-full px-1.5 text-[11px] tabular-nums', f === 'unread' && counts.unread ? 'bg-brand-orange text-white' : 'bg-muted text-muted-foreground')}>{counts[f]}</span>
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          <section className="overflow-hidden rounded-2xl border bg-card shadow-card">
            <div className="flex flex-col gap-2 border-b p-3 lg:flex-row lg:items-center">
              <div className="relative min-w-0 flex-1 lg:max-w-xs">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('notifications.search')} aria-label={t('notifications.search')} className="pl-8" />
              </div>
              <div className="grid grid-cols-2 gap-2 lg:flex">
                <Select
                  className="lg:w-48"
                  value={category}
                  onChange={setCategory}
                  aria-label={t('notifications.category')}
                  options={[{ value: 'all', label: t('notifications.allCategories') }, ...NOTIFICATION_CATEGORIES.map((c) => ({ value: c, label: t(`notifications.categories.${c}`) }))]}
                />
                <Select
                  className="lg:w-52"
                  value={level}
                  onChange={setLevel}
                  aria-label={t('notifications.level')}
                  options={[{ value: 'all', label: t('notifications.allLevels') }, ...LEVELS.map((l) => ({ value: l, label: t(`notifications.levels.${l}`) }))]}
                />
              </div>
              <span className="text-xs text-muted-foreground lg:ml-auto">{t('notifications.count', { count: rows.length, total: all.length })}</span>
            </div>

            {rows.length === 0 ? (
              <EmptyState icon={BellOff} title={all.length ? t('notifications.noMatch') : t('notifications.empty')} />
            ) : (
              <>
                {groups.map(([bucket, items]) => (
                  <div key={bucket}>
                    <p className="border-b bg-muted/50 px-4 py-1.5 text-xs font-semibold text-muted-foreground">{t(`notifications.bucket.${bucket}`)}</p>
                    <ul className="divide-y">
                      {items.map((n) => {
                        const Icon = CATEGORY_ICON[n.category]
                        const unread = !n.readAt
                        return (
                          <li key={n.id} className={cn('flex flex-col gap-3 px-4 py-3.5 sm:flex-row sm:items-start', unread && 'bg-primary/[0.035]')}>
                            <div className="flex min-w-0 flex-1 items-start gap-3">
                              <span className={cn('flex size-9 shrink-0 items-center justify-center rounded-xl', LEVEL_TONE[n.level])}>
                                <Icon className="size-4" aria-hidden />
                              </span>
                              <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                                  {unread ? <span className="size-2 shrink-0 rounded-full bg-brand-orange" aria-hidden /> : null}
                                  <p className={cn('text-sm', unread ? 'font-semibold' : 'font-medium')}>{n.title}</p>
                                  <Badge tone="outline" className="font-normal">
                                    {t(`notifications.categories.${n.category}`)}
                                  </Badge>
                                  <span className="sr-only">{unread ? t('notifications.unread') : t('notifications.read')}</span>
                                </div>
                                <p className="mt-0.5 text-sm text-muted-foreground">{n.body}</p>
                                <p className="mt-1 text-xs text-muted-foreground">
                                  {format(parseISO(n.createdAt), 'd MMM yyyy HH:mm', { locale: tr })} · {n.actor}
                                  {n.readAt ? ` · ${t('notifications.readAt', { time: format(parseISO(n.readAt), 'd MMM HH:mm', { locale: tr }) })}` : ''}
                                </p>
                              </div>
                            </div>
                            <div className="flex shrink-0 justify-end gap-1 pl-12 sm:pl-0">
                              <IconAction
                                label={unread ? t('notifications.markRead') : t('notifications.markUnread')}
                                icon={unread ? MailOpen : Mail}
                                disabled={setRead.isPending}
                                onClick={() => setRead.mutate({ id: n.id, read: unread })}
                              />
                              {n.link ? (
                                <IconAction
                                  label={t('notifications.open')}
                                  icon={ExternalLink}
                                  onClick={() => {
                                    if (unread) setRead.mutate({ id: n.id, read: true })
                                    navigate(n.link!)
                                  }}
                                />
                              ) : null}
                              <IconAction label={t('common.delete')} icon={Trash2} danger disabled={remove.isPending} onClick={() => remove.mutate(n.id, { onSuccess: () => toast.success(t('notifications.removed')) })} />
                            </div>
                          </li>
                        )
                      })}
                    </ul>
                  </div>
                ))}
                <Pager {...pager} />
              </>
            )}
          </section>
        </div>
      )}
    </>
  )
}
